import { ReactNode, createElement } from 'react';
import styled from 'styled-components';
import { ReactComponent as TableIcon } from '@elementary/common-ui/assets/16x16/table.svg';
import { ReactComponent as ColumnIcon } from '@elementary/common-ui/assets/columns.svg';
import { ReactComponent as PipelineSvg } from '@elementary/common-ui/assets/16x16/pipeline.svg';
import theme from '@elementary/common-ui/style/theme';
import {
  Flex,
  FlexCenter,
  LabelLink,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { getTestIcon } from '@elementary/common-ui/components/cellRenderers/testNameRenderer';
import { Typography } from '@mui/material';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import CopyButton from 'shared/components/CopyButton';
import { SourceType } from 'shared/types';
import { IncidentOverviewData } from '../../../types';

const IconText = styled(Flex)`
  align-items: center;
  gap: 8px;

  overflow: hidden;

  > svg {
    path {
      fill: ${({ theme }) => theme.palette.common.gray4};
    }
  }
`;

const TableIconStyled = styled(TableIcon)`
  width: 17px;
  min-width: 17px;
`;

const ColumnIconStyled = styled(ColumnIcon)`
  width: 17px;
  min-width: 17px;
`;

const PipelineIconWrapper = styled(Flex)`
  align-items: center;
  flex-shrink: 0;
`;

const PipelineIconStyled = styled(PipelineSvg)`
  width: 17px;
  min-width: 17px;
`;

type IncidentHeaderConfig = {
  resourceName?: string;
  resourceIcon?: ReactNode;
  resourceId?: string;
  onResourceClick?: () => void;
  testName?: string;
  testIcon?: ReactNode;
  onTestClick?: () => void;
};

type OpenSidePanelFn = ReturnType<typeof useSidePanelParams>['openSidePanel'];

function getIncidentHeaderConfig(
  incident: IncidentOverviewData,
  openSidePanel: OpenSidePanelFn,
): IncidentHeaderConfig {
  switch (incident.sourceType) {
    case SourceType.TEST: {
      const testType = incident.testType || '';
      const testSubType = incident.testSubType || '';
      return {
        resourceName: incident.fullyQualifiedName ?? undefined,
        resourceIcon: incident.fullyQualifiedName ? (
          incident.isColumn ? (
            <ColumnIconStyled />
          ) : (
            <TableIconStyled />
          )
        ) : undefined,
        resourceId: incident.tableAssetId ?? undefined,
        onResourceClick: incident.tableAssetId
          ? () => openSidePanel('asset', incident.tableAssetId!, undefined)
          : undefined,
        testName: incident.testName || incident.displayName,
        testIcon: (() => {
          const Icon = getTestIcon(testType, testSubType);
          return Icon ? (
            <FlexCenter>{createElement(Icon)}</FlexCenter>
          ) : undefined;
        })(),
        onTestClick: incident.sourceId
          ? () => openSidePanel('test', incident.sourceId, 'test')
          : undefined,
      };
    }
    case SourceType.MODEL:
      return {
        resourceName: incident.fullyQualifiedName ?? undefined,
        resourceIcon: incident.fullyQualifiedName ? (
          incident.isColumn ? (
            <ColumnIconStyled />
          ) : (
            <TableIconStyled />
          )
        ) : undefined,
        resourceId: incident.tableAssetId ?? undefined,
        onResourceClick: incident.tableAssetId
          ? () => openSidePanel('asset', incident.tableAssetId!, undefined)
          : undefined,
      };
    case SourceType.PIPELINE:
      return {
        resourceName:
          incident.pipelineData?.connectorName || 'Unknown connector',
        resourceIcon: (
          <PipelineIconWrapper>
            <PipelineIconStyled />
          </PipelineIconWrapper>
        ),
      };
    default:
      return {};
  }
}

type Props = {
  incident: IncidentOverviewData;
};

const IncidentOverviewHeader = ({ incident }: Props) => {
  const { openSidePanel } = useSidePanelParams();
  const config = getIncidentHeaderConfig(incident, openSidePanel);

  return (
    <Flex gap={2} alignItems="center">
      {config.testName && (
        <IconText>
          {config.testIcon && config.testIcon}

          <Flex sx={{ fontSize: '14px', overflow: 'hidden' }}>
            <OverflowTooltip tooltipContent={config.testName}>
              {config.onTestClick ? (
                <LabelLink onClick={config.onTestClick}>
                  {config.testName}
                </LabelLink>
              ) : (
                config.testName
              )}
            </OverflowTooltip>
          </Flex>
        </IconText>
      )}

      {config.resourceName && (
        <IconText>
          {config.resourceIcon}

          <Flex sx={{ overflow: 'hidden' }}>
            <OverflowTooltip tooltipContent={config.resourceName}>
              {config.onResourceClick ? (
                <LabelLink onClick={config.onResourceClick}>
                  {config.resourceName}
                </LabelLink>
              ) : (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: theme.typography.fontWeightBold,
                    textDecoration: 'underline',
                  }}
                >
                  {config.resourceName}
                </Typography>
              )}
            </OverflowTooltip>
          </Flex>

          <CopyButton
            textToCopy={config.resourceName}
            sx={{ height: '20px', width: '20px' }}
          />
        </IconText>
      )}
    </Flex>
  );
};

export default IncidentOverviewHeader;
